import { StarRatingCriterion, StarRatingDimension, StarRatingValue } from 'constants/misc'

import { Nullable } from 'models/helpers'

/** A single dimension's star rating data */
export interface StarRatingEntry {
  dimension: StarRatingDimension
  rating: StarRatingValue
  criteria: StarRatingCriterion[]
  isFavorite: boolean
  ratingText?: string
}

/**
 * Per-assignment rating entry embedded in the assignment DTO, keyed by dimension.
 * Mirrors the BE "Rating data for an assignment" object — the same shape as the
 * GET /assignment/{id}/rating response entries.
 */
export interface AssignmentRatingSummary {
  /** Rated dimension (e.g. VISUALS, CREATIVE) */
  type: string
  /** Star value given for the dimension (integer 1-5) */
  rating?: StarRatingValue
  /** Free-text comment (max 2000 characters) */
  ratingText?: string
  /** Criteria selected when the rating is below 5 stars (max 5) */
  criteria?: StarRatingCriterion[]
}

/** The complete star rating payload for an assignment */
export interface StarRatingPayload {
  assignmentId: string
  ratings: StarRatingEntry[]
}

/**
 * Maps the GET /assignment/{id}/rating response to StarRatingEntry[].
 * Only includes dimensions that exist in StarRatingDimension (skips legacy types like SERVICE).
 */
export function mapRatingResponseToEntries(
  ratings: Array<{ type: string, rating: number, ratingText: string, criteria: StarRatingCriterion[] | null }>,
  isFavouriteCT: boolean | null
): StarRatingEntry[] {
  const validDimensions = new Set<string>(Object.values(StarRatingDimension))

  return ratings
    .filter(r => validDimensions.has(r.type))
    .map(r => ({
      dimension: r.type as StarRatingDimension,
      rating: r.rating as StarRatingValue,
      criteria: r.criteria ?? [],
      isFavorite: r.type === StarRatingDimension.CREATIVE ? (isFavouriteCT ?? false) : false,
      ratingText: r.ratingText
    }))
}

/** Star rating state per dimension (used for local form state) */
export interface StarRatingDimensionState {
  rating: Nullable<StarRatingValue>
  criteria: Set<StarRatingCriterion>
  isFavorite: boolean
  comment: string
}
